/**
 * Paging a long list: which pages exist, which to offer, and what to say.
 *
 * No React, so `tests/pagination.test.ts` can assert it under bare node — and
 * no runtime imports, for the reason `source-list.ts` gives.
 */

/** The page sizes offered under a paged table. */
export const PAGE_SIZES = [10, 25, 50] as const;

/** How many pages a total makes. Never fewer than one, so "page 1 of 1" holds for nothing. */
export function pageCount(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

/**
 * A page kept within the pages there are. A list can shrink under a reader —
 * deleting a scraper deletes its runs — and somebody left on page 9 of what is
 * now 3 pages should land on page 3, not on an empty table.
 */
export function clampPage(page: number, total: number, pageSize: number): number {
  return Math.min(Math.max(1, page), pageCount(total, pageSize));
}

/** `Showing 11-20 from 134`, or what an empty list says. */
export function pageSummary(page: number, pageSize: number, total: number): string {
  if (total === 0) return "Nothing to show yet";
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  return `Showing ${first}-${last} from ${total.toLocaleString("en-US")}`;
}

/**
 * The page somebody typed into the page box, kept within the pages there are —
 * or null when what they typed is not a page number at all, so the box can put
 * the current page back rather than jumping somewhere nobody asked for.
 */
export function typedPage(text: string, lastPage: number): number | null {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) return null;
  return Math.min(Math.max(1, Number(trimmed)), lastPage);
}
